<template>
    <v-container class="hotel-data__container">
        <v-row class="hotel-data__header">
            <v-col cols="12 text-center">
                <span class="hotel-name">
                    {{ hotelName }}
                </span>
            </v-col>
            <v-col cols="12 px-4">
                <v-row class="hotel-data__page-title">
                    <span class="hotel-data__title color-grey-onfly">
                        Antes de prosseguir...
                    </span>
                    <span class="hotel-data__subtitle color-grey-onfly">
                        Por favor, confirme algumas informações sobre o hotel:
                    </span>
                </v-row>
            </v-col>
        </v-row>
        <v-form ref="hotelDataForm" @keyup.enter.native="confirmHotelData">
            <v-container class="hotel-data__form-card rounded-xl">
                <v-row class="mt-0 px-4">
                    <v-col cols="12" class="section-title pb-2">
                        <span>Dados de Contato</span>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            outlined
                            dense
                            light
                            v-model="hotelContact"
                            v-mask="contactMask"
                            :rules="phoneRules"
                            label="Telefone do Responsável"
                            required
                            placeholder="(##) ####-####"
                            ref="contact"
                            class="rounded-lg"
                            hide-details="auto"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-items-center">
                        <v-text-field
                            outlined
                            dense
                            v-model="hotelEmail"
                            :rules="emailRules"
                            label="Email do Responsável"
                            required
                            hide-details="auto"
                            class="rounded-lg"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            outlined
                            dense
                            v-model="hotelWhatsapp"
                            v-mask="whatsappMask"
                            :rules="phoneRules"
                            label="Whatsapp do Hotel"
                            required
                            hide-details="auto"
                            placeholder="(##) #####-####"
                            ref="whatsAppNumber"
                            class="rounded-lg"
                            hint="Usaremos esse número para enviar solicitações de reservas e confirmações de pagamento"
                            persistent-hint
                        ></v-text-field>
                    </v-col>
                    <v-col v-if="isPaymentTypePix" cols="12" class="section-title pt-0">
                        <span>Pix</span>
                    </v-col>
                    <v-col v-if="isPaymentTypePix" cols="12" md="6">
                        <v-radio-group
                            row
                            class="mt-1"
                            v-model="hotelPixKeyType"
                            hide-details="auto"
                        >
                            <v-radio label="CNPJ" value="cnpj"></v-radio>
                            <v-radio label="CPF" value="cpf"></v-radio>
                            <v-radio label="Celular" value="cellphone"></v-radio>
                            <v-radio label="E-mail" value="email"></v-radio>
                        </v-radio-group>
                    </v-col>
                    <v-col v-if="isPaymentTypePix" cols="12" md="6" align-self="center" class="mb-2">
                        <v-text-field
                            v-model="hotelPixKey"
                            :rules="hotelPixKeyRules"
                            v-mask="`${hotelPixKeyMask}`"
                            label="Chave Pix"
                            outlined
                            dense
                            class="rounded-lg"
                            hide-details="auto"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </v-form>
        <div class="actions-bar">
            <v-container class="bg-white pb-0 px-0">
                <v-container class="actions-bar__content-container pb-6">
                    <v-row class="d-flex justify-end">
                        <v-col cols="12" md="4">
                            <v-btn
                                depressed
                                color="success"
                                @click="confirmHotelData"
                                class="w-100 rounded-lg"
                            >
                                Confirmar
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-container>
        </div>
    </v-container>
</template>

<script>
import { isValidCnpj } from "../../../helpers/ValidationRuleHelper";

export default {
    name: "HotelData",
    props: {
        hotelData: {
            type: Object,
            required: true,
        },
    },
    data () {
        return {
            hotelName: this.hotelData.name,
            hotelContact: this.hotelData.contact ?? '',
            hotelEmail: this.hotelData.email ?? '',
            hotelWhatsapp: this.hotelData.whatsapp ?? '',
            hotelPaymentType: this.hotelData.paymentType ?? '',
            hotelPixKey: this.hotelData.pixKey ?? '',
            hotelPixKeyType: this.hotelData.pixKeyType ?? '',
            phoneRules: [
                v => !!v || 'O telefone é obrigatório',
                v => !v || !!v.match(/^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$/) || 'Informe um telefone válido'
            ],
            whatsappRules: [
                v => !!v || 'O whatsapp é obrigatório',
                v => !v || !!v.match(/^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$/) || 'Informe um whatsapp válido'
            ],
            emailRules: [
                v => !!v || 'O email é obrigatorio',
                v => !!v.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) || 'O email é inválido'
            ],
        }
    },
    computed: {
        contactMask () {
            return this.getPhoneMask(this.hotelContact);
        },
        whatsappMask () {
            return this.getPhoneMask(this.hotelWhatsapp);
        },
        hotelPixKeyRules () {
            if (this.hotelPixKeyType === 'cpf') {
                return [
                    v => !!v || 'A chave pix é obrigatória',
                    v => !!v.match('^[0-9]{3}\.[0-9]{3}\.[0-9]{3}\-[0-9]{2}$') || 'Informe um CPF válido'
                ];
            }
            if (this.hotelPixKeyType === 'cnpj') {
                return [
                    v => !!v || 'A chave pix é obrigatória',
                    v => isValidCnpj(v) || 'Cnpj inválido',
                ];
            }
            if (this.hotelPixKeyType === 'email') {
                return [
                    v => !!v || 'A chave pix é obrigatória',
                    v => !!v.match('^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$') || 'Informe um e-mail válido'
                ];
            }
            if (this.hotelPixKeyType === 'cellphone') {
                return [
                    v => !!v || 'A chave pix é obrigatória',
                    v => !v || !!v.match('^\\([0-9]{2}\\)\\ [0-9]{5}\\-[0-9]{4}$') || `Informe um celular valido`
                ];
            }
            return [];
        },
        hotelPixKeyMask () {
            if (this.hotelPixKeyType === 'cnpj') {
                return 'NN.NNN.NNN/NNNN-##';
            }
            if (this.hotelPixKeyType === 'cpf') {
                return '###.###.###-##';
            }
            if (this.hotelPixKeyType === 'cellphone') {
                return '(##) #####-####';
            }
            return '';
        },
        isPaymentTypePix () {
            return this.hotelPaymentType === 'pix';
        }
    },
    methods: {
        getPhoneMask (phoneData) {
            if (phoneData.length <= 14) {
                return '(##) ####-####';
            }

            return '(##) #####-####';
        },
        confirmHotelData () {
            if (this.$refs.hotelDataForm.validate()) {
                this.$emit('confirmed');
            }
        },
        getData() {
            return {
                contact: this.hotelContact,
                whatsapp_number: this.hotelWhatsapp,
                email: this.hotelEmail,
                pix_key: this.hotelPixKey,
                pix_key_type: this.hotelPixKeyType
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.hotel-data__page-title {
    display: flex;
    flex-direction: column;
    padding-left: 1rem;
    margin-bottom: 0;
    margin-top: 2rem;
}

.hotel-data__header {
    .hotel-name {
        font-size: 1.5rem;
        font-weight: 600;
        color: #064574;
    }
}

.hotel-data__form-card {
    border: 1px solid #F5F5F6;
    margin-top: 1rem;
    margin-bottom: 2rem;
    padding: 1rem 0.5rem 1.5rem 0.5rem;
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
    background-color: #ffffff;

    .section-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: #064574;
        padding-bottom: 2px;
    }
}

.hotel-data__title {
    font-size: 1.125rem;
    font-weight: 600;
}

.hotel-data__subtitle {
    font-size: 0.875rem;
}

.actions-bar {
    max-width: 1440px;
    margin: 0 auto;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    padding: 6px 12px 0 6px;
}

.actions-bar__content-container {
    padding: 1.5rem 1rem 0.5rem 1rem;
    background: white;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    border-top-left-radius: 2rem;
    border-top-right-radius: 2rem;
}
</style>
